import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import AppApi from '@/apis/app-api';
import { AutoReplyApi, AutoReplyUpdateBody } from '@/apis/auto-reply';
import { baseUrl } from '@/consts/route';
import { UseCustomMutationOptions } from '@/types/react-query';
import { cleanArray } from '@/utils/cleanArray';

import { useAuth } from './useAuth';

// GET / - 댓글 자동등록 관리 대상 조회
export const useAutoReplyShops = (params?: { user_id?: string; shop_id?: string }) => {
  const { accessToken, userId } = useAuth();
  const api = new AppApi(baseUrl);
  const autoReplyApi = new AutoReplyApi(api);
  const _userId = params?.user_id || userId;

  const getAutoReplyShops = async () => {
    if (!accessToken) return null;
    api.setup(accessToken);
    const response = await autoReplyApi.getAutoReplyShops({ ...params, user_id: _userId });
    return response?.data;
  };

  return useQuery({
    queryKey: cleanArray(['auto_reply_shops', _userId, params?.shop_id]),
    queryFn: getAutoReplyShops,
    enabled: !!accessToken && !!_userId,
  });
};

export const useUserAutoReply = () => {
  const { accessToken, userId } = useAuth();
  const api = new AppApi(baseUrl);
  const autoReplyApi = new AutoReplyApi(api);

  const getAutoReplyShops = async () => {
    if (!accessToken) return null;
    api.setup(accessToken);
    const response = await autoReplyApi.getUserAutoReplyPreference(userId);

    return response?.data;
  };

  return useQuery({
    queryKey: cleanArray(['auto_reply_shops', userId]),
    queryFn: getAutoReplyShops,
    enabled: !!accessToken && !!userId,
  });
};

// PATCH /user/:user_id - 사용자 기준 일괄 자동등록 상태 업데이트
export const useUpdateUserAutoReply = ({
  onSuccess,
  onError,
  ...options
}: UseCustomMutationOptions<
  Awaited<ReturnType<AutoReplyApi['updateUserAutoReply']>>,
  Error,
  AutoReplyUpdateBody
> = {}) => {
  const { accessToken, userId } = useAuth();
  const api = new AppApi(baseUrl);
  const autoReplyApi = new AutoReplyApi(api);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update_user_auto_reply'],
    mutationFn: async (body: AutoReplyUpdateBody) => {
      if (!accessToken) throw new Error('Access token is required');
      api.setup(accessToken);
      return await autoReplyApi.updateUserAutoReply(userId, body);
    },
    async onSuccess(data, variables, onMutateResult, context) {
      // 자동등록 목록 쿼리 무효화하여 재조회
      await queryClient.invalidateQueries({ queryKey: ['auto_reply_shops'] });
      await onSuccess?.(data, variables, onMutateResult, context);
    },
    async onError(error, variables, onMutateResult, context) {
      await onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
};
